import { isEqual } from 'lodash';
import { useEffect } from 'react';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { getTableViewState, resolveColumnVisibility } from './helpers/tableViewState';

export const useTableViewManager = (tableSettings: ViewSettings, { syncGrid = false } = {}) => {
  const { settings, setSetting, setMultipleSettings, settingsReady } = useSettingsContext();
  const { tableName = 'UnnamedTable', apiRef, defaultColumnVisibilityModel, setColumnVisibilityModel } = tableSettings;
  const { views, activeViewId, activeView } = getTableViewState(settings, tableName);
  const activeModel = activeView.Active ?? null;
  const savedModel = activeView.Saved ?? null;
  const activeViewName = activeView.NameActive ?? activeView.NameSaved ?? (activeViewId === '0' ? 'Default' : 'New View');
  const savedViewName = activeView.NameSaved ?? (activeViewId === '0' ? 'Default' : 'New View');
  const effectiveModel = resolveColumnVisibility(activeModel, defaultColumnVisibilityModel);
  const canEdit = settingsReady !== false;
  const isDirty = !isEqual(activeModel, savedModel) || activeViewName !== savedViewName;
  const viewsList = Object.entries(views).map(([id, view]) => ({ id, name: view.NameActive ?? view.NameSaved ?? (id === '0' ? 'Default' : id) }));
  const viewPath = ['TableColumnVisibility', tableName, activeViewId];

  useEffect(() => {
    // CustomDataGrid is controlled by shared settings. Imperative sync there would
    // echo programmatic profile/default changes back into saved user preferences.
    if (syncGrid && !setColumnVisibilityModel && canEdit) apiRef?.current?.setColumnVisibilityModel(effectiveModel);
  }, [syncGrid, setColumnVisibilityModel, canEdit, apiRef, effectiveModel]);

  const handleViewChange = (id: string) => {
    if (canEdit && views[id]) setSetting(['CurrentTableView', tableName], id);
  };

  const save = () => {
    if (!canEdit) return;
    setMultipleSettings([
      { key: [...viewPath, 'Saved'], value: activeModel },
      { key: [...viewPath, 'NameSaved'], value: activeViewName.trim() || 'Default' },
    ]);
  };

  const discard = () => {
    if (!canEdit) return;
    setMultipleSettings([
      { key: [...viewPath, 'Active'], value: savedModel },
      { key: [...viewPath, 'NameActive'], value: savedViewName },
    ]);
  };

  const restoreDefaults = () => {
    if (canEdit) setSetting([...viewPath, 'Active'], null);
    // Keep Saved intact: Discard can undo this reset until the user saves it.
  };

  const addView = (rawName = 'New View') => {
    if (!canEdit) return;
    const id = generateGuid();
    const name = rawName.trim() || 'New View';
    setMultipleSettings([
      { key: ['TableColumnVisibility', tableName, id], value: { NameActive: name, NameSaved: name, Active: { ...effectiveModel }, Saved: { ...effectiveModel } } },
      { key: ['CurrentTableView', tableName], value: id },
    ]);
  };

  const renameView = (id: string, name: string) => {
    if (canEdit && id !== '0' && views[id]) setSetting(['TableColumnVisibility', tableName, id, 'NameActive'], name.trim() || 'New View');
  };

  const deleteView = (id: string) => {
    if (!canEdit || id === '0' || !views[id]) return;
    setMultipleSettings([
      { key: ['TableColumnVisibility', tableName, id], delete: true },
      ...(id === activeViewId ? [{ key: ['CurrentTableView', tableName], value: '0' }] : []),
    ]);
  };

  return { activeViewId, activeViewName, viewsList, handleViewChange, canEdit, isDirty, save, discard, restoreDefaults, addView, renameView, deleteView };
};

const generateGuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = (Math.random() * 16) | 0;
  return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
});

interface ViewSettings {
  tableName?: string;
  apiRef?: any;
  columnVisibilityModel?: GridColumnVisibilityModel;
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
  setColumnVisibilityModel?: (model: GridColumnVisibilityModel) => void;
}
